import { Textarea, TextareaProps } from '@chakra-ui/react'
import { mdiBookOpenVariantOutline } from '@mdi/js'
import { t } from 'i18next'
import { PrimitiveAtom, useAtom } from 'jotai'
import React from 'react'

import SquareButton from '@/components/buttons/square'
import { Box } from '@/components/ui/box'
import { currentEnv } from '@/env/vars'
import { chatSettingsAtom, openPromptLibraryDrawer } from '@/store/gigiChat/ChatAtom'
import { getVisualAgentInfo } from '@/store/gigiChat/Visuals'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'

import UploadArea from './liveAgent/upload'

interface IAutoResizeInputProps extends TextareaProps {
    isInputFullExpanded: boolean
    handleMessage: (question: string, fileURLManager?: Array<string>) => void
}

const AutoResizeInput = React.forwardRef<HTMLTextAreaElement, IAutoResizeInputProps>((props, ref) => {
    const textAreaProps: Partial<IAutoResizeInputProps> = { ...props }
    delete textAreaProps.isInputFullExpanded
    delete textAreaProps.handleMessage
    const [chatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const agentInfo = getVisualAgentInfo(chatSettings?.processingModeAgent?.name)
    const supportsAttachmentsInWritingMode =
        (currentEnv.features.isEnabled('useAgent') &&
            chatSettings?.processingStrategy === 'QA' &&
            chatSettings?.processingModeAgent &&
            agentInfo?.supportsAttachments) ||
        (currentEnv.features.isEnabled('imageUnderstanding') && chatSettings?.processingStrategy === 'Writing')
    const isPromptLibraryEnabled = chatSettings?.processingStrategy === 'Writing' && currentEnv.features.isEnabled('promptLibrary')
    const handleClick = () => {
        openPromptLibraryDrawer()
    }
    const getPaddingLeft = () => {
        if (supportsAttachmentsInWritingMode && isPromptLibraryEnabled) {
            return '76px'
        } else if (supportsAttachmentsInWritingMode || isPromptLibraryEnabled) {
            return '56px'
        } else {
            return '16px'
        }
    }

    const promptLibraryIsDisabled = chatSettings?.processingStrategyWriting.mode === 'promptLibrary'
    return props.isInputFullExpanded ? (
        <Textarea
            paddingTop="16px"
            minH="56px"
            maxH="100%"
            overflow="auto"
            w="100%"
            h="100%"
            resize="none"
            ref={ref}
            variant="gsk"
            padding="0"
            border="0"
            style={{ outline: '0', caretColor: 'var(--chakra-colors-orange-400)' }}
            {...textAreaProps}
        />
    ) : (
        <Box position="relative" w="100%">
            <>
                {isPromptLibraryEnabled && (
                    <Box position="absolute" top="8px" left={supportsAttachmentsInWritingMode ? '40px' : '10px'} zIndex={2}>
                        <SquareButton
                            dataID="prompt-library-textfield"
                            isDisabled={promptLibraryIsDisabled}
                            icon={mdiBookOpenVariantOutline}
                            label={t('chat.promptLib.label')}
                            onClick={handleClick}
                            tooltipPosition="top"
                            isIconButton={true}
                            dataTestId="chat-prompt-library-button"
                        />
                    </Box>
                )}
                {supportsAttachmentsInWritingMode && <UploadArea handleMessage={props.handleMessage} />}
                <Textarea
                    autoresize
                    paddingTop="16px"
                    minH="56px"
                    maxH="330px"
                    overflow="auto"
                    w="100%"
                    resize="none"
                    ref={ref}
                    rows={1}
                    variant="gsk"
                    pl={getPaddingLeft()}
                    style={{ caretColor: 'var(--chakra-colors-orange-400)' }}
                    {...textAreaProps}
                    data-testid="chat-user-input-textarea"
                />
            </>
        </Box>
    )
})

export default AutoResizeInput
